import { useState } from "react"

import { useQuery } from "@tanstack/react-query"
import { Minus, Plus } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { TableCell, TableRow } from "@/components/ui/table"

import { getBalanceRegionTrips } from "../api/getBalanceRegionTrips"

import RegionBalanceTrips from "./RegionBalanceTrips"

import type { BalanceRegion } from "../types/balanceMapTypes"

interface RegionBalanceRowProps {
  region: BalanceRegion
  hoveredRegionId?: number | null
  onRegionHoverChange?: (regionId: number | null) => void
}

export default function RegionBalanceRow({
  region,
  hoveredRegionId,
  onRegionHoverChange,
}: RegionBalanceRowProps) {
  const [showTrips, setShowTrips] = useState<boolean>(false)

  const { data } = useQuery({
    queryKey: ["balanceRegionsTrips", { id: region.id }],
    queryFn: () => getBalanceRegionTrips(region.id),
    refetchInterval: 15000,
  })

  return (
    <>
      <TableRow
        data-state={hoveredRegionId === region.id ? "selected" : undefined}
        onMouseEnter={() => onRegionHoverChange?.(region.id)}
        onMouseLeave={() => onRegionHoverChange?.(null)}
      >
        <TableCell className="p-2">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              className="size-8 hover:bg-white"
              onClick={() => setShowTrips(!showTrips)}
            >
              {showTrips ? <Minus /> : <Plus />}
            </Button>
            <span
              className="inline-block size-3 shrink-0 rounded-full border"
              style={{ backgroundColor: region.color ?? "#4a90d9" }}
            />
            {region.name ?? region.id}
          </div>
        </TableCell>
        <TableCell className="text-right p-4">
          {data ? data.today.in.length : <Skeleton className="w-full h-[20px]" />}
        </TableCell>
        <TableCell className="text-right p-4">
          {data ? data.today.out.length : <Skeleton className="w-full h-[20px]" />}
        </TableCell>
        <TableCell className="text-right p-4 bg-muted/50">
          {data ? data.tomorrow.in.length : <Skeleton className="w-full h-[20px]" />}
        </TableCell>
        <TableCell className="text-right p-4 bg-muted/50">
          {data ? data.tomorrow.out.length : <Skeleton className="w-full h-[20px]" />}
        </TableCell>
      </TableRow>
      {showTrips && data && <RegionBalanceTrips trips={data} />}
    </>
  )
}
